"use client";

import React, { useState } from "react";
import { FeatherChevronRight, FeatherFacebook, FeatherInstagram, FeatherLinkedin, FeatherTwitter } from "@subframe/core";
import { TextField } from "@/ui/components/TextField";
import { Button } from "@/ui/components/Button";
import { FeatherSend } from "@subframe/core";
import logoLight from "@/assets/logo-light.png";
import mainBackgroundMobile from "@/assets/main-background-mobile.webp";
import mainBackgroundTablet from "@/assets/main-background-tablet.webp";
import mainBackgroundDesktop from "@/assets/main-background-desktop.webp";
import mainBackgroundOriginal from "@/assets/main-background.webp";
import { apiClient } from "./api/client";
import { toast, Toaster } from "sonner";
import { SEO } from "./components/SEO";
import { IconButton } from "./ui/components/IconButton";

export default function App() {

  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleSubscribe = async () => {
    const validEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/.test(email);
    if (!validEmail) {
      setIsValid(false);
      // toast.error("Please enter a valid email address");
      return;
    }

    setIsValid(true);

    try {
      setIsLoading(true);
      await apiClient.post('/early-subscribe', { email });
      toast.success("Thanks for joining the pit crew. See you at the green light. 🏁");
      setDisabled(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO />
      <div className="flex w-full flex-col items-center justify-center gap-4 sm:gap-6 md:gap-8 lg:gap-12 overflow-hidden bg-default-background px-4 sm:px-6 md:px-8 py-4 sm:py-8 md:py-16 lg:py-24 h-dvh relative">
        <Toaster position="top-center" richColors/>
              <div className="flex flex-col items-center justify-center gap-3 sm:gap-4 md:gap-6 lg:gap-8 z-10 w-full max-w-6xl">
        <img src={logoLight} alt="Mobilista" className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12" />
        <div className="flex items-center gap-1 rounded-md border border-solid border-brand-200 pl-2 pr-1 sm:pl-3 sm:pr-2 py-1 backdrop-blur-sm">
          <span className="whitespace-nowrap text-sm sm:text-base text-body-bold text-brand-primary">
            Mobilista is Coming
          </span>
          <FeatherChevronRight className="text-sm sm:text-base text-body text-brand-primary" />
        </div>
        <div className="flex w-full max-w-[768px] flex-col items-start gap-2 sm:gap-3 md:gap-4">
          <span className="w-full text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-[56px] font-[700] leading-tight sm:leading-tight md:leading-tight lg:leading-tight xl:leading-[60px] text-default-font text-center -tracking-[0.035em] px-2">
            {/* Build Your Dream Ride. Before Anyone Else. */}
            Forget Spreadsheets.<br className="hidden sm:block"/> Mobilista Tracks Your Build the Way You Actually Work.
          </span>
          <span className="w-full whitespace-pre-wrap text-base sm:text-lg md:text-xl lg:text-2xl xl:text-[24px] font-[500] leading-relaxed sm:leading-relaxed md:leading-relaxed lg:leading-relaxed xl:leading-[32px] text-subtext-color text-center -tracking-[0.025em] px-2">
            {
              "A platform made for enthusiasts, project lovers, and modding fanatics. Mobilista helps you manage every nut and bolt - before it hits the road."
            }
          </span>
        </div>
        <div className="flex w-full max-w-[448px] flex-col items-center gap-2 sm:gap-3 md:gap-4 rounded-lg border border-solid border-brand-200 px-4 sm:px-6 py-4 sm:py-6 backdrop-blur-sm mx-4">
          <TextField
            className={`h-auto w-full flex-none ${!isValid ? 'border-red-500' : ''}`}
            variant="filled"
            error={!isValid}
            label=""
            helpText=""
          >
            <TextField.Input
              placeholder="Enter your email for early access"
              value={email}
              type="email"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setIsValid(true);
                setDisabled(false);
                setEmail(event.target.value);
              }}
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  if (isLoading || disabled) return;
                  handleSubscribe();
                }
              }}
            />
          </TextField>
          {!isValid && (
              <p className="text-xs sm:text-sm text-caption font-caption text-red-500 text-left">
                *Please enter a valid email address
              </p>
            )}
          <Button
            className="h-10 w-full flex-none"
            size="large"
            icon={<FeatherSend />}
            disabled={isLoading || disabled}
            loading={isLoading}
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              handleSubscribe();
            }}
          >
            Subscribe for early access
          </Button>
          <span className="text-xs sm:text-sm text-caption font-caption text-subtext-color text-center">
            Be the first to know when we launch. No spam, just updates.
          </span>
          <div className="flex items-center gap-2 pt-2">
            <IconButton
              icon={<FeatherInstagram />}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                window.open('https://www.instagram.com/mobilista.io/', '_blank');
              }}
            />
            <IconButton
              icon={<FeatherTwitter />}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                window.open('https://x.com/mobilista_io', '_blank');
              }}
            />
            <IconButton
              icon={<FeatherFacebook />}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                window.open('https://www.facebook.com/profile.php?id=61578171651104', '_blank');
              }}
            />
            <IconButton
              icon={<FeatherLinkedin />}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                window.open('https://www.linkedin.com/company/mobilista-io/', '_blank');
              }}
            />
          </div>
        </div>
      </div>
      {/* Blur placeholder */}
      <div 
        className={`w-full h-full absolute inset-0 transition-opacity duration-500 ${
          imageLoaded ? 'opacity-0' : 'opacity-100'
        }`}
        style={{
          backgroundImage: `url(${mainBackgroundMobile})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(20px)',
          transform: 'scale(1.1)'
        }}
      />
      
      {/* Responsive background images */}
      <picture className="w-full h-full absolute inset-0">
        <source 
          srcSet={mainBackgroundMobile} 
          media="(max-width: 768px)" 
        />
        <source 
          srcSet={mainBackgroundTablet} 
          media="(min-width: 769px) and (max-width: 1024px)" 
        />
        <source 
          srcSet={mainBackgroundDesktop} 
          media="(min-width: 1025px) and (max-width: 1440px)" 
        />
        <img
          className={`w-full h-full object-cover transition-opacity duration-500 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          src={mainBackgroundOriginal}
          alt="Background"
          onLoad={() => setImageLoaded(true)}
          loading="eager"
        />
      </picture>
      <div className="flex items-start absolute inset-0 bg-gradient-to-t from-default-background to-transparent" />
      <div className={`flex items-start absolute inset-0 bg-cover bg-center mix-blend-overlay opacity-5`} style={{backgroundImage: `url(${mainBackgroundOriginal})`}} />
      <div className="flex items-start absolute inset-0 bg-[linear-gradient(45deg,transparent_0%,rgba(64,_64,_64,_0.1)_15%,transparent_30%)]" />
      <div className="flex items-start absolute inset-0 bg-gradient-to-b from-transparent to-default-background" />
    </div>
    </>
  );
}
